import { usePermissionStore } from '@/stores/modules/permission'
import { computed, unref } from 'vue'
import { recursion } from '@/utils/tools'
import { isArray, isString } from '@/utils/is'
import { SystemCodeType } from '@/enums/global'

/**
 * 关于接口menuId的设置方法
 * 优先级最高： 调用接口时传入header: {menuId: xxx}  适合单独接口设置
 *            如何使用？—————— 在页面发起请求之前通过getMenuId(传入code或path)获取对应的id后，通过配置header传入
 * 优先级较高： headerMenuId, headerTableCode  适合跳转到详情页时，所有接口都依赖同一个menuId或tableCode的场景
 *            如何使用？ —————— 在进入页面时，接口调用前 通过setHeaderMenuId(getMenuId({code: 'CustomerManage'}))使用，注意：headerMenuId将在beforeRoute路由钩子里面清除
 *                            tableCode用于无法获取到菜单id的自定义应用详情，直接从route query中去到tableCode,通过setHeaderTableCode(code)使用，headerTableCode的优先级比headerMenuId高
 * 优先级最低： 请求拦截器中getMenuId获取的当前菜单id
 *            如何使用？ —————— 无需配置，自动获取
 *
 */

export function useResource() {
  const permissionStore = usePermissionStore()

  const resourceList = computed(() => {
    return permissionStore.resourceList
  })

  const resourceListExtend = computed(() => {
    return permissionStore.resourceListExtend
  })

  const currentMenu = computed(() => {
    return permissionStore.currentMenu
  })

  const headerMenuId = computed(() => {
    return permissionStore.headerMenuId
  })

  const headerTableCode = computed(() => {
    return permissionStore.headerTableCode
  })

  function setHeaderMenuId(id: string) {
    permissionStore.setHeaderMenuId(id)
  }

  const headerAppCode = computed(() => {
    return permissionStore.headerAppCode
  })

  function setMenuAppCode(id: string) {
    permissionStore.setMenuAppCode(id)
  }

  function getCurrentMenu() {
    return unref(currentMenu)
  }

  function getTableCode(): string {
    return unref(currentMenu).tableCode
  }

  // 判断是否有菜单权限
  function getMenuAuth(code: string) {
    let auth = false
    recursion(unref(permissionStore.allMenuList), menu => {
      if (menu.code === code) auth = true
    })
    return auth
  }

  // 判断是否有按钮权限
  function getAuth(flag: string) {
    if (flag === 'debugger') return true
    if (unref(resourceList)?.length) {
      const res = unref(resourceList).find((el: any) => el.code === flag)
      return !!res
    } else if (unref(resourceListExtend)?.length) {
      const res = unref(resourceListExtend).find((el: any) => el.code === flag)
      return !!res
    }
    return false
  }

  /**
   * 获取menuId
   * @param params 不传返回当前菜单的id，传code或path返回对应的menuId
   */
  function getMenuId(
    params: { code?: string; path?: string; appCode?: string } | undefined = undefined
  ) {
    if (!params) return unref(currentMenu).id
    let targetMenu: Recordable = {}
    recursion(permissionStore.allMenuList, menu => {
      if (params.code && menu.code === params.code) targetMenu = { ...menu }
      if (params.path && menu.path === params.path) targetMenu = { ...menu }
      if (params.appCode && menu.appCode === params.appCode) targetMenu = { ...menu }
    })
    return targetMenu?.id
  }

  /**
   * 获取菜单的appCode
   * @param params 不传返回当前菜单的id，传code或path返回对应的menuId
   */
  function getMenuAppCode(
    params: { code?: string; path?: string; name?: string } | undefined = undefined
  ) {
    if (!params) return unref(currentMenu).appCode
    let targetMenu: Recordable = {}
    recursion(permissionStore.allMenuList, menu => {
      if (params.code && menu.code === params.code) targetMenu = { ...menu }
      if (params.path && menu.path === params.path) targetMenu = { ...menu }
      if (params.name && menu.name === params.name) targetMenu = { ...menu }
    })
    return targetMenu?.appCode
  }

  // 获取页面条件筛选配置
  function getQueryOption() {
    return unref(currentMenu).queryOption
  }

  // 获取页面新增默认参数配置
  function getDefaultValues() {
    return unref(currentMenu).defaultValues
  }

  // 设置权限继承
  function setResourceExtend(list: any[] | undefined = undefined) {
    permissionStore.setResourceListExtend(list)
  }

  /**
   * 获取某一个菜单的信息（menuId,resourceList等）
   * @param systemCode
   * @param menuPath 菜单页面路径值path， 如果传字符串，就返回一个菜单页面的按钮权限，如果传数组，就遍历不同的菜单权限，依次返回（场景：一个页面的权限使用的是同一个应用包，但是需要两个菜单页面的权限）
   * @param isSetResourceExtend 是否将菜单权限缓存下来，缓存下来的使用场景是：当前页面只依赖于一个应用包，如果一个页面中存在多个应用包的按钮权限，就不要缓存，缓存会导致互相污染
   */
  async function getMenuInfo(
    systemCode: SystemCodeType,
    menuPath: string | string[],
    isSetResourceExtend: boolean = true
  ) {
    let targetMenu: any = {
      id: '',
      resourceList: [],
      systemCode,
      tableCode: ''
    }
    try {
      if (isString(menuPath)) {
        targetMenu = getPageAuthByMenuCode(unref(permissionStore.allMenuList), systemCode, menuPath)
        if (isSetResourceExtend) {
          // 如果是跨应用包获取权限，isSetResourceExtend就设为false，防止污染本应用包的权限
          setResourceExtend(targetMenu.resourceList || [])
        }
      }
      if (isArray(menuPath)) {
        // 如果是同一个应用下的多个菜单权限，menuPath就传数组
        return getPageAuthByMenuCode(unref(permissionStore.allMenuList), systemCode, menuPath)
      }
      return targetMenu
    } catch (e) {
      return targetMenu
    }
  }
  // 通过页面路径获取菜单按钮权限
  function getPageAuthByMenuCode(
    allSystemCodeAuth: any,
    systemCode: string,
    menuPath: string | string[]
  ) {
    const targetMenu: any = {
      id: '',
      resourceList: [],
      systemCode,
      tableCode: ''
    }
    try {
      if (isString(menuPath)) {
        recursion(allSystemCodeAuth, menu => {
          if (menu.path.indexOf(menuPath) > -1 && !menu.defined) {
            targetMenu.id = menu.id
            targetMenu.resourceList = menu.resourceVOList || []
            targetMenu.tableCode = menu.appCode
          }
        })
        return targetMenu
      }
      if (isArray(menuPath)) {
        // 如果是同一个应用下的多个菜单权限，menuPath就传数组
        const resArray: any = []
        recursion(allSystemCodeAuth, menu => {
          const menuPathStr = menuPath.join(',')
          const menuArr = menu.path.split('/')
          if (
            !menu.defined &&
            menuArr.length &&
            (menuArr[0] || menuArr[1]) &&
            menuPathStr.indexOf(menuArr[0] || menuArr[1]) > -1
          ) {
            resArray.push({
              id: menu.id,
              resourceList: menu.resourceVOList || [],
              tableCode: menu.appCode,
              path: menu.path
            })
          }
        })

        return resArray
      }
    } catch (error) {
      return targetMenu
    }
  }
  // 通过菜单Code获取按钮权限
  function getAuthByMenuCode(menuCode: string, code: string) {
    let currentMenu: Recordable = {}
    recursion(permissionStore.allMenuList, menu => {
      if (menu.code === menuCode) currentMenu = { ...menu }
    })
    if (currentMenu?.resourceList?.length) {
      const res = unref(currentMenu?.resourceList).find((el: any) => el.code === code)
      return !!res
    }
  }

  // 根据appCode获取当前菜单信息
  function getMenuInfoByAppCode(appCode: string) {
    let currentMenu: Recordable = {}
    recursion(permissionStore.allMenuList, menu => {
      if (menu.appCode === appCode) currentMenu = { ...menu }
    })
    return currentMenu
  }
  // 根据name获取当前菜单信息
  function getMenuInfoByName(name: string) {
    if (!name) return [unref(currentMenu)]
    const targetMenu: Recordable[] = []
    recursion(permissionStore.allMenuList, menu => {
      if (name && menu.name === name) targetMenu.push(menu)
    })
    return targetMenu
  }

  function getMenuInfoByCode(code: string) {
    if (!code) return [unref(currentMenu)]
    const targetMenu: Recordable[] = []
    recursion(permissionStore.allMenuList, menu => {
      if (code && menu.code === code) targetMenu.push(menu)
    })
    return targetMenu
  }

  return {
    getAuth,
    getMenuId,
    getTableCode,
    setResourceExtend,
    getMenuInfo,
    getDefaultValues,
    getQueryOption,
    getMenuAuth,
    getAuthByMenuCode,
    headerMenuId,
    setHeaderMenuId,
    headerTableCode,
    getMenuAppCode,
    headerAppCode,
    getMenuInfoByCode,
    setMenuAppCode,
    getMenuInfoByAppCode,
    getMenuInfoByName,
    getCurrentMenu
  }
}
